import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TopNavigation from './components/TopNavigation';
import HeroSection from './components/HeroSection';
import AmenitiesSection from './components/AmenitiesSection';
import LocationSection from './components/LocationSection';
import PricingSection from './components/PricingSection';
import ReferralProgramSection from './components/ReferralProgramSection';
import TestimonialsSection from './components/TestimonialsSection';
import FooterSection from './components/FooterSection';


const LandingPage = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [leadData, setLeadData] = useState(null);

  const sections = [
    { id: 'hero', label: 'Inicio', icon: 'Home' },
    { id: 'amenities', label: 'Amenidades', icon: 'Sparkles' },
    { id: 'location', label: 'Ubicación', icon: 'MapPin' },
    { id: 'pricing', label: 'Precios', icon: 'DollarSign' },
    { id: 'referral', label: 'Programa', icon: 'TrendingUp' },
    { id: 'testimonials', label: 'Testimonios', icon: 'MessageSquare' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);

      const sectionElements = sections?.map(section => ({
        id: section?.id,
        element: document.getElementById(section?.id)
      }));
const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  if (ref) localStorage.setItem("referral_code", ref);
  
      const currentSection = sectionElements?.find(section => {
        if (section?.element) {
          const rect = section?.element?.getBoundingClientRect();
          return rect?.top <= 150 && rect?.bottom >= 150;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection?.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element?.getBoundingClientRect()?.top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLeadSubmit = (data) => {
    setLeadData(data);
    setShowSuccessModal(true);
    setTimeout(() => {
      setShowSuccessModal(false);
    }, 5000);
  };

  return (
    <>
      <Helmet>
        <title>Residencial 3 Volcanes - Invierte en tu Futuro | Lotes Premium en Arequipa</title>
        <meta 
          name="description" 
          content="Últimos 12 lotes disponibles en Residencial 3 Volcanes, Arequipa. Desde S/ 18,000 con financiamiento flexible. Plusvalía garantizada +35% anual. ¡Reserva hoy!" 
        />
        <meta name="keywords" content="lotes arequipa, residencial 3 volcanes, inversión inmobiliaria arequipa, terrenos arequipa, millennials arequipa, plusvalía arequipa, financiamiento lotes" />
        <meta property="og:title" content="Residencial 3 Volcanes - Invierte en tu Futuro" />
        <meta property="og:description" content="Últimos 12 lotes desde S/ 18,000. Financiamiento flexible. Plusvalía +35% anual. ¡Reserva ahora!" />
        <meta property="og:image" content="/assets/images/logo-3-1768280661456.jpg" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
        {/* Top Navigation */}
        <TopNavigation scrollToSection={scrollToSection} />

        {/* Floating Navigation - Hidden on mobile for better UX */}
        <motion.nav 
          className="fixed left-2 top-1/2 transform -translate-y-1/2 z-50 hidden xl:block"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1 }}
        >
          <div className="bg-white p-2 space-y-2 shadow-2xl border-2 border-orange-200 rounded-2xl">
            {sections?.map((section) => (
              <motion.button
                key={section?.id}
                onClick={() => scrollToSection(section?.id)}
                className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 touch-target ${
                  activeSection === section?.id
                    ? 'bg-gradient-to-br from-orange-600 to-red-600 text-white shadow-xl scale-110 border-2 border-orange-300'
                    : 'hover:bg-orange-50 hover:scale-105 border-2 border-transparent hover:border-orange-200'
                }`}
                title={section?.label}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name={section?.icon} size={20} />
              </motion.button>
            ))}
          </div>
        </motion.nav>

        {/* Hero Section */}
        <div id="hero">
          <HeroSection onLeadSubmit={handleLeadSubmit} />
        </div>

        {/* Amenities Section */}
        <div id="amenities">
          <AmenitiesSection />
        </div>

        {/* Location Section */}
        <div id="location">
          <LocationSection />
        </div>

        {/* Pricing Section */}
        <div id="pricing">
          <PricingSection />
        </div>

        {/* Referral Program Section */}
        <div id="referral">
          <ReferralProgramSection />
        </div>

        {/* Testimonials Section */}
        <div id="testimonials">
          <TestimonialsSection />
        </div>

        {/* Footer Section */}
        <FooterSection />

        {/* Scroll to Top Button - Mobile optimized */}
        {showScrollTop && (
          <motion.button
            onClick={scrollToTop}
            className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-600 to-red-600 text-white rounded-full shadow-2xl hover:shadow-orange-300 transition-all flex items-center justify-center touch-target group border-2 border-orange-300"
            aria-label="Volver arriba"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Icon name="ArrowUp" size={24} className="group-hover:scale-110 transition-transform" />
          </motion.button>
        )}

        {/* Trust Badge - Hidden on mobile for cleaner UI */}
        <motion.div 
          className="fixed bottom-8 left-4 sm:left-8 z-40 hidden lg:block"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
        >
          <div className="bg-white p-3 sm:p-4 shadow-2xl border-2 border-emerald-300 rounded-2xl">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg shadow-lg">
                <Icon name="Shield" size={20} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-black text-gray-900">Proyecto Certificado</p>
                <p className="text-xs text-gray-600 font-semibold">Licencia Municipal Vigente</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Success Modal */}
        {showSuccessModal && leadData && (
          <motion.div 
            className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div 
              className="absolute inset-0 bg-background/90 backdrop-blur-md"
              onClick={() => setShowSuccessModal(false)}
            />
            <motion.div 
              className="relative card max-w-md w-full elevation-5"
              initial={{ scale: 0.8, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <div className="text-center mb-6">
                <motion.div 
                  className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 mb-4 shadow-xl border-2 border-emerald-300"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                >
                  <Icon name="CheckCircle" size={40} className="text-white" />
                </motion.div>
                <h3 className="text-3xl font-heading font-black text-gray-900 mb-2">
                  ¡Solicitud Enviada!
                </h3>
                <p className="text-gray-600 font-semibold text-lg">
                  Gracias por confiar en Residencial 3 Volcanes
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="p-4 rounded-xl bg-gradient-to-r from-gray-50 to-orange-50 border-2 border-gray-200">
                  <p className="text-xs text-gray-600 font-semibold mb-1">Nombre</p>
                  <p className="text-gray-900 font-bold text-lg">{leadData?.nombre}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-r from-gray-50 to-orange-50 border-2 border-gray-200">
                  <p className="text-xs text-gray-600 font-semibold mb-1">Email</p>
                  <p className="text-gray-900 font-bold">{leadData?.email}</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-r from-gray-50 to-orange-50 border-2 border-gray-200">
                  <p className="text-xs text-gray-600 font-semibold mb-1">Teléfono</p>
                  <p className="text-gray-900 font-bold">{leadData?.telefono}</p>
                </div>
              </div>

              <div className="p-5 rounded-xl bg-gradient-to-r from-orange-50 to-emerald-50 border-2 border-orange-300 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="Clock" size={24} className="text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="font-black text-gray-900 mb-1">⏱️ Respuesta en menos de 2 horas</p>
                    <p className="text-sm text-gray-700 font-semibold">
                      Un asesor especializado se contactará contigo para coordinar tu visita 
                      al proyecto y resolver todas tus consultas.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                variant="default"
                fullWidth
                size="lg"
                onClick={() => setShowSuccessModal(false)}
                className="shadow-lg"
              >
                <Icon name="CheckCircle" size={20} className="mr-2" />
                Entendido
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default LandingPage;